/**
 * @fileoverview Organism: view, edit and delete a single tracked purchase.
 * @module components/organisms/PurchaseDetailsModal
 */

import { useState } from "react"

import { Button, Icon, ProgressBar } from "@/components/atoms"
import { ConfirmDialog, Modal } from "@/components/molecules"
import {
	computeAmortisation,
	formatCents,
	formatRatio,
} from "@/lib/domain/amortisation"
import type { Purchase, PurchaseId, PurchasePatch } from "@/types/types"

import { PurchaseForm } from "../PurchaseForm/PurchaseForm"

import "./PurchaseDetailsModal.css"

/** Props accepted by {@link PurchaseDetailsModal}. */
export interface PurchaseDetailsModalProps {
	/** The purchase being inspected. */
	readonly purchase: Purchase
	/** Called to dismiss the modal without side effects. */
	readonly onClose: () => void
	/** Applies a confirmed edit. */
	readonly onSave: (id: PurchaseId, patch: PurchasePatch) => void
	/** Applies a confirmed deletion. */
	readonly onDelete: (id: PurchaseId) => void
	/** Records uses; a negative delta removes some. */
	readonly onRecordUse: (id: PurchaseId, delta: number) => void
}

/**
 * Detail sheet for one purchase: figures, edit form and delete, each
 * mutation gated behind an explicit confirmation step.
 *
 * @param props - The purchase and the actions available on it.
 * @returns The rendered modal, or the confirmation dialog covering it while
 *   an edit or a deletion awaits confirmation.
 *
 * @example
 * <PurchaseDetailsModal
 *   purchase={polo}
 *   onClose={close}
 *   onSave={save}
 *   onDelete={remove}
 *   onRecordUse={recordUse}
 * />
 */
export function PurchaseDetailsModal({
	purchase,
	onClose,
	onSave,
	onDelete,
	onRecordUse,
}: PurchaseDetailsModalProps) {
	const [isEditing, setIsEditing] = useState(false)
	const [pendingPatch, setPendingPatch] = useState<PurchasePatch | null>(null)
	const [confirmingDelete, setConfirmingDelete] = useState(false)

	if (confirmingDelete) {
		return (
			<ConfirmDialog
				title={`Supprimer ${purchase.label} ?`}
				message="Cette action est définitive : l’historique d’utilisation sera perdu."
				confirmLabel="Supprimer"
				danger
				onConfirm={() => {
					onDelete(purchase.id)
					onClose()
				}}
				onCancel={() => {
					setConfirmingDelete(false)
				}}
			/>
		)
	}

	if (pendingPatch !== null) {
		return (
			<ConfirmDialog
				title="Enregistrer les modifications ?"
				message={`${purchase.label} sera mis à jour avec ces nouvelles informations.`}
				confirmLabel="Enregistrer"
				onConfirm={() => {
					onSave(purchase.id, pendingPatch)
					onClose()
				}}
				onCancel={() => {
					setPendingPatch(null)
				}}
			/>
		)
	}

	if (isEditing) {
		return (
			<Modal title={`Modifier ${purchase.label}`} onClose={onClose}>
				<PurchaseForm
					submitLabel="Valider les modifications"
					initialValue={{
						label: purchase.label,
						priceCents: purchase.priceCents,
						targetUses: purchase.targetUses,
						...(purchase.category === undefined
							? {}
							: { category: purchase.category }),
					}}
					onSubmit={draft => {
						setPendingPatch(draft)
					}}
				/>
			</Modal>
		)
	}

	const amortisation = computeAmortisation(purchase)

	return (
		<Modal title={purchase.label} onClose={onClose}>
			<div className="purchase-details">
				{purchase.category !== undefined && (
					<span className="purchase-details__category">
						{purchase.category}
					</span>
				)}

				<p className="purchase-details__price">
					{formatCents(purchase.priceCents)}
				</p>

				<ProgressBar
					ratio={amortisation.ratio}
					label={`Amortissement de ${purchase.label}`}
				/>

				<dl className="purchase-details__figures">
					<div>
						<dt>Utilisation</dt>
						<dd>
							{purchase.currentUses} / {purchase.targetUses}
						</dd>
					</div>
					<div>
						<dt>Coût / usage</dt>
						<dd>{formatCents(amortisation.costPerUseCents)}</dd>
					</div>
					<div>
						<dt>Amortie</dt>
						<dd>
							{amortisation.isAmortised ? "🟢 " : ""}
							{formatRatio(amortisation.ratio)}
						</dd>
					</div>
				</dl>

				<div className="purchase-details__uses">
					<Button
						onClick={() => {
							onRecordUse(purchase.id, 1)
						}}
					>
						+1 utilisation
					</Button>
					{purchase.currentUses > 0 && (
						<Button
							variant="ghost"
							onClick={() => {
								onRecordUse(purchase.id, -1)
							}}
							ariaLabel={`Retirer une utilisation de ${purchase.label}`}
						>
							−1
						</Button>
					)}
				</div>

				<div className="purchase-details__manage">
					<Button
						variant="secondary"
						onClick={() => {
							setIsEditing(true)
						}}
					>
						<Icon name="pencil" size={16} />
						Modifier
					</Button>
					<Button
						variant="ghost"
						danger
						onClick={() => {
							setConfirmingDelete(true)
						}}
					>
						<Icon name="trash" size={16} />
						Supprimer
					</Button>
				</div>
			</div>
		</Modal>
	)
}
